import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Building2, Plus, MoreVertical, Users, Briefcase,
  Trash2, Edit, Loader2, Check, X, Search, 
  BarChart3, UserPlus, Globe
} from 'lucide-react';

export default function OrganisationManagement() {
  const { token, user } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    type: 'mfo',
    adminEmail: '',
    adminName: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganisations();
    }
  }, [token, isSuperAdmin]);

  const loadOrganisations = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.organisations) {
        setOrganisations(data.organisations);
      }
    } catch (error) {
      console.error('Failed to load organisations:', error);
      toast.error('Failed to load organisations');
    }
    setLoading(false);
  };

  const handleAddOrganisation = async () => {
    if (!formData.name) {
      toast.error('Organisation name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Organisation created successfully');
        setShowAddModal(false);
        setFormData({ name: '', domain: '', type: 'mfo', adminEmail: '', adminName: '' });
        loadOrganisations();
      } else {
        toast.error(data.detail || 'Failed to create organisation');
      }
    } catch (error) {
      toast.error('Failed to create organisation');
    }
    setSaving(false);
  };

  const handleUpdateOrganisation = async () => {
    if (!selectedOrg) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations/${selectedOrg.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          domain: formData.domain
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Organisation updated successfully');
        setShowEditModal(false);
        setSelectedOrg(null);
        loadOrganisations();
      } else {
        toast.error(data.detail || 'Failed to update organisation');
      }
    } catch (error) {
      toast.error('Failed to update organisation');
    }
    setSaving(false);
  };

  const handleDeleteOrganisation = async () => {
    if (!selectedOrg) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations/${selectedOrg.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Organisation deactivated');
        setShowDeleteModal(false);
        setSelectedOrg(null);
        loadOrganisations();
      } else {
        toast.error(data.detail || 'Failed to deactivate organisation');
      }
    } catch (error) {
      toast.error('Failed to deactivate organisation');
    }
    setSaving(false);
  };

  const handlePermanentDeleteOrganisation = async () => {
    if (!selectedOrg || confirmText !== selectedOrg.name) {
      toast.error('Please type the organisation name to confirm');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations/${selectedOrg.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Organisation permanently deleted');
        setShowPermanentDeleteModal(false);
        setSelectedOrg(null);
        setConfirmText('');
        loadOrganisations();
      } else {
        toast.error(data.detail || 'Failed to delete organisation');
      }
    } catch (error) {
      toast.error('Failed to delete organisation');
    }
    setSaving(false);
  };

  const openPermanentDeleteModal = (org) => {
    setSelectedOrg(org);
    setConfirmText('');
    setShowPermanentDeleteModal(true);
  };

  const handleAddAdmin = async () => {
    if (!selectedOrg || !formData.adminEmail) {
      toast.error('Email is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organisations/${selectedOrg.id}/add-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.adminEmail,
          name: formData.adminName,
          role: 'org_admin'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Admin added successfully');
        setShowAddAdminModal(false);
        setFormData({ ...formData, adminEmail: '', adminName: '' });
        loadOrganisations();
      } else {
        toast.error(data.detail || 'Failed to add admin');
      }
    } catch (error) {
      toast.error('Failed to add admin');
    }
    setSaving(false);
  };

  const openEditModal = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      domain: org.domain || '',
      adminEmail: '',
      adminName: ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (org) => {
    setSelectedOrg(org);
    setShowDeleteModal(true);
  };

  const openAddAdminModal = (org) => {
    setSelectedOrg(org);
    setFormData({ ...formData, adminEmail: '', adminName: '' });
    setShowAddAdminModal(true);
  };

  const filteredOrgs = organisations.filter(o => 
    o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto mb-4 text-[#8C8078] opacity-50" />
        <p style={{ color: 'var(--text-secondary)' }}>Super Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organisation-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg" style={{ color: 'var(--text-primary)' }}>
            Organisations
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Manage client organisations and their administrators
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ name: '', domain: '', type: 'mfo', adminEmail: '', adminName: '' });
            setShowAddModal(true);
          }}
          className="bg-[#4A3B32] hover:bg-[#3A2E25] text-white"
          data-testid="add-org-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Organisation
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
        <Input
          placeholder="Search organisations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="org-search-input"
        />
      </div>

      {/* Organisation Cards */}
      <div className="grid gap-4">
        {filteredOrgs.length === 0 ? (
          <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" style={{ color: 'var(--text-secondary)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>No organisations found</p>
          </div>
        ) : (
          filteredOrgs.map((org) => (
            <div
              key={org.id}
              className="rounded-xl border p-5 hover:shadow-md transition-shadow"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#4A3B32] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>
                        {org.name}
                      </h3>
                      {org.active !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">
                          <X className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </div>
                    {org.domain && (
                      <p className="text-sm flex items-center gap-1 mt-1" style={{ color: 'var(--text-secondary)' }}>
                        <Globe className="w-3 h-3" /> {org.domain}
                      </p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Users className="w-4 h-4" />
                        <span>{org.stats?.users || 0} users</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Briefcase className="w-4 h-4" />
                        <span>{org.stats?.clients || 0} clients</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <BarChart3 className="w-4 h-4" />
                        <span>{org.stats?.entities || 0} entities</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAddAdminModal(org)}
                    title="Add Admin"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Admin
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(org)}
                    title="Edit Organisation"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteModal(org)}
                    title="Deactivate Organisation"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={org.id === 'org-default'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Organisation Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Create Organisation
            </DialogTitle>
            <DialogDescription>
              Create a new organisation and optionally add an initial admin user.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organisation Name *</Label>
              <Input
                id="org-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ABC Wealth Advisors"
                data-testid="add-org-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="org-domain">Domain (optional)</Label>
              <Input
                id="org-domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="e.g., abcwealth.com.au"
                data-testid="add-org-domain"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="org-type">Organisation Type *</Label>
              <Select 
                value={formData.type || 'mfo'} 
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger data-testid="add-org-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sfo">Single Family Office</SelectItem>
                  <SelectItem value="mfo">Multi-Family Office</SelectItem>
                  <SelectItem value="advice">Advice Firm</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8C8078]">
                {formData.type === 'sfo' 
                  ? 'Single Family Office - One client with simplified dashboard'
                  : formData.type === 'advice'
                  ? 'Advice Firm - Multiple clients with full dashboard'
                  : 'Multi-Family Office - Multiple clients with full dashboard'
                }
              </p>
            </div>
            
            <div className="border-t pt-4 mt-4" style={{ borderColor: 'var(--border-color)' }}>
              <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
                Initial Admin User (optional)
              </p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="admin-email">Admin Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    placeholder="admin@company.com"
                    data-testid="add-org-admin-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin-name">Admin Name</Label>
                  <Input
                    id="admin-name"
                    value={formData.adminName}
                    onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                    placeholder="John Smith"
                    data-testid="add-org-admin-name"
                  />
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                If provided, an admin user will be created and sent a welcome email with login credentials.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddOrganisation}
              disabled={saving}
              className="bg-[#4A3B32] hover:bg-[#3A2E25] text-white"
              data-testid="add-org-submit"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Organisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organisation Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Organisation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-org-name">Organisation Name</Label>
              <Input
                id="edit-org-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-org-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-org-domain">Domain</Label>
              <Input
                id="edit-org-domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                data-testid="edit-org-domain"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateOrganisation}
              disabled={saving}
              className="bg-[#4A3B32] hover:bg-[#3A2E25] text-white"
              data-testid="edit-org-submit"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Modal */}
      <Dialog open={showAddAdminModal} onOpenChange={setShowAddAdminModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add Admin to {selectedOrg?.name}
            </DialogTitle>
            <DialogDescription>
              Create a new admin user for this organisation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-admin-email">Email Address *</Label>
              <Input
                id="new-admin-email"
                type="email"
                value={formData.adminEmail}
                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                placeholder="admin@company.com"
                data-testid="new-admin-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-admin-name">Full Name</Label>
              <Input
                id="new-admin-name"
                value={formData.adminName}
                onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                placeholder="John Smith"
                data-testid="new-admin-name"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdminModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAdmin}
              disabled={saving}
              className="bg-[#4A3B32] hover:bg-[#3A2E25] text-white"
              data-testid="add-admin-submit"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Organisation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{selectedOrg?.name}</strong>? 
              All users in this organisation will be deactivated and unable to log in.
              <br /><br />
              <span className="text-amber-600">This can be reversed later.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                openPermanentDeleteModal(selectedOrg);
              }}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              Permanent Delete Instead
            </Button>
            <AlertDialogAction
              onClick={handleDeleteOrganisation}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-delete-org"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Confirmation Modal */}
      <AlertDialog open={showPermanentDeleteModal} onOpenChange={setShowPermanentDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ Permanent Deletion</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to <strong className="text-red-600">permanently delete</strong> the organisation 
                <strong> {selectedOrg?.name}</strong>.
              </p>
              <p className="text-red-600 font-medium">
                This will delete ALL data including:
              </p>
              <ul className="list-disc ml-5 text-sm space-y-1">
                <li>All users ({selectedOrg?.stats?.users || 0} users)</li>
                <li>All clients ({selectedOrg?.stats?.clients || 0} clients)</li>
                <li>All individuals, entities, advisers</li>
                <li>All tasks, links, and activity logs</li>
              </ul>
              <p className="font-bold text-red-600">This action cannot be undone!</p>
              <div className="pt-2">
                <Label className="text-sm">Type "{selectedOrg?.name}" to confirm:</Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={selectedOrg?.name}
                  className="mt-1"
                  data-testid="confirm-org-name-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePermanentDeleteOrganisation}
              disabled={confirmText !== selectedOrg?.name || saving}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="confirm-permanent-delete-org"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Permanently Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
